import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import AboutPage from '../components/AboutPage';
import ArtistProfile from '../components/artist-profile/ArtistProfile_refactored';
import BannerSection from '../components/BannerSection';
import CollaborationDetailPage from '../components/CollaborationDetailPage';
import CompetitionBanner from '../components/CompetitionBanner';
import GallerySection from '../components/GallerySection';
import HeroSection from '../components/HeroSection';
import LoginPage from '../components/LoginPage';
import MilestonePage from '../components/MilestonePage';
import NotificationCenter from '../components/NotificationCenter';
import ProtectedRoute from '../components/ProtectedRoute';
import RegisterPage from '../components/register-page/RegisterPage_refactored';
import SupabaseConnectionTest from '../components/SupabaseConnectionTest';
import TAGMePage from '../components/TAGMePage';
import ProjectionPage from '../components/projection/ProjectionPage';
import CollaborationTestPage from '../components/testing/CollaborationTestPage';
import NotificationTest from '../components/testing/NotificationTest';
import TestMockAPI from '../components/testing/TestMockAPI';
import UploadGuidelines from '../components/upload-guidelines/UploadGuidelines_refactored';
import UploadFormGuard from '../components/UploadFormGuard';
import UploadPage from '../components/UploadPage';
import WorkDetailPage from '../components/work-detail/WorkDetailPage_refactored';
import AdminPanel from '../pages/AdminPanel';
import CoFuture2025 from '../pages/competitions/CoFuture2025';
import MajorAggregationPage from '../pages/MajorAggregationPage';
import EditProfile from '../pages/settings/EditProfile.tsx';
import TagAggregationPage from '../pages/TagAggregationPage';
import { useNavigation } from '../utils/navigation';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import TermsOfServicePage from '../pages/TermsOfServicePage';
import HelpCenterPage from '../pages/HelpCenterPage';
import PostPosterPage from '../components/PostPosterPage';
import { useAuth } from '../context/AuthContext';

// 主页组件
const HomePage = () => (
  <>
    <HeroSection />
    <BannerSection />
    <CompetitionBanner />
    <GallerySection />
  </>
);

// 首页路由组件：任何用户（含未登录、移动端）都可浏览首页
// 2026-04-17：按授权解除移动端未登录强制跳转逻辑（原始版本请查 git history）
const ConditionalHomeRoute = () => {
  const { loading } = useAuth();

  // 加载中时显示加载状态，避免 Auth 初始化时闪屏
  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-tag-blue mx-auto'></div>
          <p className='mt-4 text-gray-600'>Loading...</p>
        </div>
      </div>
    );
  }

  return <HomePage />;
};

// 路由配置
const AppRoutes = () => {
  const { navigateToCollaboration, navigateToMilestone } = useNavigation();

  // 处理collaboration点击事件
  const handleCollaborationClick = project => {
    navigateToCollaboration(project);
  };

  // 处理milestone点击事件
  const handleMilestoneClick = milestone => {
    navigateToMilestone(milestone);
  };

  return (
    <Routes>
      {/* 登录页面 - 不需要保护 */}
      <Route path='/login' element={<LoginPage />} />

      {/* 注册页面 - 不需要保护 */}
      <Route path='/register' element={<RegisterPage />} />

      {/* 法务页面 - 公开访问 */}
      <Route path='/privacy' element={<PrivacyPolicyPage />} />
      <Route path='/terms' element={<TermsOfServicePage />} />
      <Route path='/help' element={<HelpCenterPage />} />

      {/* 主页 - 所有用户均可浏览 */}
      <Route path='/' element={<ConditionalHomeRoute />} />

      {/* About页面 - 不需要保护 */}
      <Route path='/about' element={<AboutPage />} />

      {/* Post a Poster - 完全公开，无需登录 */}
      <Route path='/post-poster' element={<PostPosterPage />} />

      {/* 个人页面 - 需要保护 */}
      <Route
        path='/me'
        element={
          <ProtectedRoute>
            <ArtistProfile />
          </ProtectedRoute>
        }
      />

      {/* 作品详情页 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route path='/work/:id' element={<WorkDetailPage />} />

      {/* 艺术家档案页 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route path='/artist/:id' element={<ArtistProfile />} />

      {/* TAGMe页面 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route
        path='/tagme'
        element={
          <TAGMePage
            onCollaborationClick={handleCollaborationClick}
            onMilestoneClick={handleMilestoneClick}
          />
        }
      />

      {/* Projection页面 - 完全獨立 */}
      <Route path='/projection' element={<ProjectionPage />} />

      {/* 里程碑页面 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route path='/tagme/milestone/:id' element={<MilestonePage />} />

      {/* 合作详情页面 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route
        path='/tagme/collaboration/:id'
        element={<CollaborationDetailPage />}
      />

      {/* CoFuture2025竞赛页面 - 不需要保护 */}
      <Route path='/tagme/co-future-2025' element={<CoFuture2025 />} />

      {/* 上传指南页面 - 不需要保护 */}
      <Route path='/upload/guidelines' element={<UploadGuidelines />} />

      {/* 上传表单页面 - 需要保护且必须确认指南 */}
      <Route
        path='/upload/form'
        element={
          <ProtectedRoute>
            <UploadFormGuard>
              <UploadPage />
            </UploadFormGuard>
          </ProtectedRoute>
        }
      />

      {/* 兼容旧的上传路由 */}
      <Route path='/upload' element={<UploadGuidelines />} />

      {/* 通知中心页面 - 需要保护 */}
      <Route
        path='/notifications'
        element={
          <ProtectedRoute>
            <NotificationCenter />
          </ProtectedRoute>
        }
      />

      {/* 通知测试页面 - 需要保护 */}
      <Route
        path='/notification-test'
        element={
          <ProtectedRoute>
            <NotificationTest />
          </ProtectedRoute>
        }
      />

      {/* 设置页面 - 需要保护 */}
      <Route
        path='/settings/edit-profile'
        element={
          <ProtectedRoute>
            <EditProfile />
          </ProtectedRoute>
        }
      />

      {/* Mock API 测试页面 - 临时添加 */}
      <Route
        path='/test-mock-api'
        element={
          <ProtectedRoute>
            <TestMockAPI />
          </ProtectedRoute>
        }
      />

      {/* Collaboration 测试页面 - 临时添加 */}
      <Route
        path='/test-collaboration'
        element={
          <ProtectedRoute>
            <CollaborationTestPage />
          </ProtectedRoute>
        }
      />

      {/* Supabase 连接测试页面 - 临时添加 */}
      <Route path='/test-supabase' element={<SupabaseConnectionTest />} />

      {/* 标签聚合页面 - 公开浏览（2026-04-17 按授权解除登录限制，SEO 友好） */}
      <Route path='/t/:slug' element={<TagAggregationPage />} />

      {/* 统一聚合页面 - 公开浏览（2026-04-17 按授权解除登录限制） */}
      <Route path='/aggregation' element={<TagAggregationPage />} />

      {/* SEO友好的专业聚合路由 - 公开浏览 */}
      <Route path='/major/:major' element={<TagAggregationPage />} />

      {/* SEO友好的软件聚合路由 - 公开浏览 */}
      <Route path='/software/:software' element={<TagAggregationPage />} />

      {/* 专业聚合页面 - 公开浏览 (保留兼容性) */}
      <Route path='/major-aggregation' element={<MajorAggregationPage />} />

      {/* 超级管理员面板 - 特殊权限 */}
      <Route path='/admin-panel' element={<AdminPanel />} />

      {/* 默认重定向到主页 */}
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  );
};

export default AppRoutes;
